import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalVisit } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/OntarioObstetricalVisit";
import { IObstetricalFileSubsequentVisitsTableRowProps, ObstetricalFileSubsequentVisitsTableRow } from "../../../../Common/Subcomponent/ObstetricalFileSubsequentVisitsTableRow";

interface IOntarioObstetricalFileSubsequentVisitsTableRowProps extends IObstetricalFileSubsequentVisitsTableRowProps<OntarioObstetricalFileForm, OntarioObstetricalVisit> { }

class OntarioObstetricalFileSubsequentVisitsTableRow extends ObstetricalFileSubsequentVisitsTableRow<OntarioObstetricalFileForm, OntarioObstetricalVisit> {
    protected readonly urineProteinKey = "UrineProtein";
    protected readonly symphysisToFundalHeightCentimetersKey = "SymphysisToFundalHeightCentimeters";

    protected get columnKeys(): string[] {
        return [
            this.dateKey,
            this.gestationalAgeKey,
            this.weightKey,
            this.bloodPressureKey,
            this.urineProteinKey,
            this.symphysisToFundalHeightCentimetersKey,
            this.presentationKey,
            this.fetalHearthRateKey,
            this.fetalMovementKey,
            this.commentsKey,
            this.nextVisitKey,
            this.initialsKey
        ];
    }

    protected get fieldLimits(): { [columnKey: string]: number } {
        return {
            [this.weightKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_PrepregnancyWeight.MaxChars,
            [this.fetalHearthRateKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_FetalHeartRate.MaxChars,
            [this.fetalMovementKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_FetalMovement.MaxChars,
            [this.presentationKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_Presentation.MaxChars,
            [this.urineProteinKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_UrineProtein.MaxChars,
            [this.symphysisToFundalHeightCentimetersKey]: OntarioObstetricalFileFieldLimits.Page3.SubsequentVisits_SymphysisToFundalHeightCentimeters.MaxChars,
            [this.nextVisitKey]: 18,
            [this.initialsKey]: 18
        };
    }

    protected get weightUnit(): WeightUnit {
        return this.props.form.Metadata.subsequentVisitsTableWeightUnit;
    }

    protected renderCell(columnKey: string): JSX.Element {
        const visit = this.props.visit;

        switch (columnKey) {
            case this.urineProteinKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderAutoCompleteTextbox({
                        value: visit.UrineProtein,
                        autoCompleteItems: [cacheStore.GetString("NegativeAbbrevLabel", null, Locale.en), "+1", "+2", "+3", "+4"].map(p => new SearchableItemModelT<string>(p, p, p)),
                        onAutoCompleteSelected: (code, model, value) => this.props.onChange(this.mutateVisit(v => v.UrineProtein = value)),
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.UrineProtein = value)),
                        className: "table-textbox",
                        maxLength: this.fieldLimits[this.urineProteinKey],
                        disableMatchAutoSorting: true,
                        listWidth: "51px"
                    })}
                </td>
            );
            case this.symphysisToFundalHeightCentimetersKey: return (
                <td key={columnKey}>
                    {this.controlRenderer.renderTextbox({
                        value: visit.SymphysisToFundalHeightCentimeters?.toString(),
                        onChange: value => this.props.onChange(this.mutateVisit(v => v.SymphysisToFundalHeightCentimeters = value && Number(value))),
                        className: "table-textbox",
                        isNumeric: true,
                        maxLength: this.fieldLimits[this.symphysisToFundalHeightCentimetersKey]
                    })}
                </td>
            );
            default: return super.renderCell(columnKey);
        }
    }

    protected mutateVisitDate(mutate: (visit: OntarioObstetricalVisit) => void): OntarioObstetricalVisit {
        return this.mutateVisit(v => {
            mutate(v);

            v.GestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                this.props.form.Common.FinalEstimatedDateOfBirth,
                v.Date
            );
        });
    }
}

export { IOntarioObstetricalFileSubsequentVisitsTableRowProps, OntarioObstetricalFileSubsequentVisitsTableRow };
